import { useState } from "react";
import { Plus, Trash2, ArrowLeft, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAdmin, useList } from "./AdminApp";
import { PageTitle } from "./Layout";
import { useCrud } from "../../lib/adminApi";
import { tr } from "../../lib/i18n";
import { img, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { I18nInput, ImageUpload, SlugField } from "../../components/admin/Fields";

function AlbumPhotos({ album, onBack }) {
  const [f, setF] = useState(album);
  const { save } = useCrud("gallery_albums");
  const { data: all = [] } = useList("photos");
  const photos = useCrud("photos");
  const [newImg, setNewImg] = useState("");
  const list = all.filter((p) => p.album_id === f.id);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const addPhoto = async (url) => { setNewImg(url); if (url && (await photos.save({ album_id: f.id, image: url, order: list.length }, true))) setNewImg(""); };
  return (
    <>
      <PageTitle title={f.id ? tr(f.title) : "Yeni albüm"} action={<Button variant="ghost" onClick={onBack}><ArrowLeft size={15} className="mr-1" />Albümler</Button>} />
      <form className="bg-white border rounded-xl p-5 grid md:grid-cols-2 gap-5" onSubmit={async (e) => { e.preventDefault(); const r = await save({ ...f, slug: f.slug ?? "" }); if (r) setF(r); }} data-testid="gallery-album-form">
        <div className="space-y-4">
          <I18nInput label="Albüm adı" value={f.title} onChange={set("title")} testid="gallery-album-title" />
          <I18nInput label="Açıklama" value={f.description} onChange={set("description")} multiline testid="gallery-album-description" />
          <SlugField value={f.slug} onChange={set("slug")} source={f.title?.tr} prefix="/galeri/" testid="gallery-album-slug" />
        </div>
        <div className="space-y-4">
          <ImageUpload label="Kapak" value={f.cover} onChange={set("cover")} testid="gallery-album-cover" alt={f.title?.tr} />
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.status === "published"} onCheckedChange={(v) => set("status")(v ? "published" : "draft")} />Yayında</label>
          <Button data-testid="gallery-album-save">Kaydet</Button>
        </div>
      </form>
      {f.id && (
        <div className="mt-8">
          <h2 className="font-semibold mb-3">Fotoğraflar ({list.length})</h2>
          <div className="bg-white border rounded-xl p-5 mb-4"><ImageUpload label="Fotoğraf ekle" value={newImg} onChange={addPhoto} testid="gallery-photo-upload" alt={f.title?.tr} /></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {list.map((p) => (
              <div key={p.id} className="relative group" data-testid={`gallery-photo-${p.id}`}>
                <img src={img(p.image)} alt="" loading="lazy" decoding="async" className="w-full aspect-square object-cover rounded-lg" />
                <button onClick={() => photos.remove(p.id, "Fotoğraf")} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} className="text-red-600" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default function GalleryAdmin() {
  const { settings, saveSection } = useAdmin();
  const { data = [] } = useList("gallery_albums");
  const { remove } = useCrud("gallery_albums");
  const [editing, setEditing] = useState(null);
  if (editing) return <AlbumPhotos album={editing} onBack={() => setEditing(null)} />;
  const enabled = !!settings.features?.gallery_enabled;
  const toggle = async (v) => {
    try { await saveSection("features", { ...settings.features, gallery_enabled: v }); toast.success(v ? "Resim galerisi açıldı" : "Resim galerisi kapatıldı"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageTitle title="Resim galerisi" sub="Varsayılan olarak kapalıdır. Açtıktan sonra Menü'den 'Hazır bölüm → Resim Galerisi' ekleyin."
        action={<Button onClick={() => setEditing({ title: { tr: "" }, status: "published" })} data-testid="new-gallery-album-button"><Plus size={15} className="mr-1" />Yeni albüm</Button>} />
      <label className="flex items-center gap-3 bg-white border rounded-xl p-5 mb-6 text-sm font-medium">
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="gallery-enabled-switch" /> Resim galerisi {enabled ? "açık" : "kapalı"}
      </label>
      <div className="bg-white border rounded-xl divide-y">
        {!data.length && <p className="p-6 text-sm text-stone-400">Henüz albüm yok.</p>}
        {data.map((a) => (
          <div key={a.id} className="flex items-center gap-4 px-5 py-3" data-testid={`gallery-album-row-${a.slug}`}>
            <p className="flex-1 font-medium">{tr(a.title)} <span className="text-xs text-stone-400">/galeri/{a.slug}</span></p>
            <Button size="icon" variant="ghost" onClick={() => setEditing(a)}><Pencil size={14} /></Button>
            <Button size="icon" variant="ghost" onClick={() => remove(a.id, "Albüm")}><Trash2 size={14} className="text-red-500" /></Button>
          </div>
        ))}
      </div>
    </>
  );
}
